import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import {
  DEFAULT_DAILY_RULE,
  applyEffectiveRuleEdit,
  canonicalSchedulePhases,
  firstRecordedDate,
  isActiveOn,
  isSelectableOutcome,
  replaceScheduleTimeline,
  type Category,
  type Entry,
  type MatrixDocument,
  type Operation,
  type RepeatRule,
  type SelectableOutcome,
} from '../../packages/domain/src/index'
import { createFixtureDocument, FIXTURE_TODAY } from './fixture'
import { useOffline } from '../offline'
import { getSupabaseClient, loadRemoteOwnerDocument, saveRemoteOwnerStructure, type RemoteOwnerDocument } from '../offline/supabase'
import type { OwnerRevision, TimelineOperation } from '../features/operations/contracts'

export interface OperationDraft {
  categoryId: string
  name: string
  private: boolean
  startDate?: string
}

interface OwnerMatrixContextValue {
  document: MatrixDocument
  trustedToday: string
  mode: 'synthetic-preview' | 'remote-owner'
  revision: OwnerRevision
  structureSync: {
    phase: 'synced' | 'saving' | 'error'
    message: string
    recovery?: 'retry' | 'reload' | 'reconcile'
  }
  securedBatchBlocker: string | null
  accumulationAnchor: string | null
  audit: readonly string[]
  setOutcome: (operationId: string, date: string, outcome: SelectableOutcome | null) => void
  addCategory: (name: string) => void
  renameCategory: (id: string, name: string) => void
  deleteCategory: (id: string) => void
  addOperation: (draft: OperationDraft) => void
  updateOperation: (id: string, draft: OperationDraft) => void
  deleteOperation: (id: string) => void
  addRuleVersion: (operationId: string, effectiveFrom: string, rule: RepeatRule) => void
  setTarget: (target: number) => void
  setAnchor: (next: string, auditMessage: string) => void
  replaceDocument: (next: MatrixDocument, auditMessage: string) => void
  commitEntryBatch: (transform: (current: MatrixDocument) => MatrixDocument, auditMessage: string) => void
  acceptRevision: (revision: OwnerRevision) => void
  refreshRevision: () => Promise<OwnerRevision>
  retryStructureSync: () => Promise<void>
  reloadAuthoritativeStructure: () => Promise<void>
  assertSecuredBatchReady: () => void
  securedBatchSnapshot: () => { document: MatrixDocument; revision: OwnerRevision }
  commitTimelineOperation: (operation: TimelineOperation, revision: OwnerRevision) => void
}

const OwnerMatrixContext = createContext<OwnerMatrixContextValue | null>(null)

function slug(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'item'
}

function uniqueId(prefix: string, name: string, existing: readonly string[]): string {
  const base = `${prefix}-${slug(name)}`
  let next = base
  let index = 2
  while (existing.includes(next)) next = `${base}-${index++}`
  return next
}

function closeActivePeriods(periods: readonly { from: string; toExclusive: string | null }[], on: string) {
  return periods.flatMap((period) => {
    if (period.toExclusive !== null) return [period]
    if (period.from >= on) return []
    return [{ ...period, toExclusive: on }]
  })
}

function serializedStructure(document: MatrixDocument): string {
  return JSON.stringify({ target: document.target, categories: document.categories, operations: document.operations })
}

export function structuralSaveFailureMessage(error: unknown): string {
  const reason = error instanceof Error ? error.message : String(error)
  if (/stale_owner_structure|another device/i.test(reason)) return 'Another device changed owner structure. Reload authoritative data before retrying; your draft remains on screen.'
  if (/timeline_preview_required/i.test(reason)) return 'This change requires its protected preview workflow. Reload authoritative data; your draft remains recoverable on screen.'
  if (/session|jwt|unauthor/i.test(reason)) return 'Your session cannot save this structural draft. Sign in again; your draft remains on screen.'
  return 'The structural draft was not saved. Retry when connected, or reload authoritative data; your draft remains on screen.'
}

function structuralSaveRecovery(error: unknown): 'retry' | 'reload' {
  const reason = error instanceof Error ? error.message : String(error)
  return /stale_owner_structure|timeline_preview_required|another device/i.test(reason) ? 'reload' : 'retry'
}

export function OwnerMatrixProvider({ children }: { children: ReactNode }) {
  const { snapshot: entrySyncSnapshot, online } = useOffline()
  const configuredOwnerRef = useRef(getSupabaseClient() !== null)
  const [document, setDocument] = useState<MatrixDocument>(createFixtureDocument)
  const [accumulationAnchor, setAccumulationAnchor] = useState<string | null>(() => firstRecordedDate(createFixtureDocument(), FIXTURE_TODAY))
  const [trustedToday, setTrustedToday] = useState(FIXTURE_TODAY)
  const [mode, setMode] = useState<'synthetic-preview' | 'remote-owner'>('synthetic-preview')
  const [revision, setRevision] = useState<OwnerRevision>({ structureVersion: 0, scoreRevision: 0 })
  const [structureSync, setStructureSync] = useState<OwnerMatrixContextValue['structureSync']>({ phase: 'synced', message: 'Changes saved.' })
  const [audit, setAudit] = useState<string[]>(['Synthetic owner document created'])
  const [ownerLoad, setOwnerLoad] = useState<'loading' | 'ready' | 'error'>(configuredOwnerRef.current ? 'loading' : 'ready')
  const structureVersionRef = useRef(0)
  const revisionRef = useRef(revision)
  const structureSyncPhaseRef = useRef<OwnerMatrixContextValue['structureSync']['phase']>('synced')
  const savedStructureRef = useRef('')
  const documentRef = useRef(document)
  const documentMutationRef = useRef(0)
  const modeRef = useRef(mode)
  const trustedTodayRef = useRef(trustedToday)
  const structureSaveInFlightRef = useRef<Promise<void> | null>(null)
  const structureSaveGenerationRef = useRef(0)
  const structureRecoveryRef = useRef<'retry' | 'reload' | 'reconcile' | null>(null)
  const unresolvedStructureConflictRef = useRef<RemoteOwnerDocument | null>(null)
  const entrySyncSnapshotRef = useRef(entrySyncSnapshot)
  const onlineRef = useRef(online)

  useEffect(() => { documentRef.current = document }, [document])
  useEffect(() => { modeRef.current = mode }, [mode])
  useEffect(() => { trustedTodayRef.current = trustedToday }, [trustedToday])
  entrySyncSnapshotRef.current = entrySyncSnapshot
  onlineRef.current = online
  revisionRef.current = revision

  const entrySyncAllowsStructure = () => onlineRef.current &&
    entrySyncSnapshotRef.current.phase === 'synced' && entrySyncSnapshotRef.current.pending === 0

  const securedBatchBlockerNow = () => modeRef.current !== 'remote-owner' ? null
    : !onlineRef.current ? 'Batch changes require an internet connection.'
    : entrySyncSnapshotRef.current.pending > 0 ? `Wait for ${entrySyncSnapshotRef.current.pending} queued entry change${entrySyncSnapshotRef.current.pending === 1 ? '' : 's'} to sync.`
    : entrySyncSnapshotRef.current.phase !== 'synced' ? 'Wait for entry sync to finish before starting a batch change.'
    : structureSyncPhaseRef.current !== 'synced' ? 'Wait for structural changes to finish syncing before starting a batch change.'
    : serializedStructure(documentRef.current) !== savedStructureRef.current ? 'Wait for structural changes to finish syncing before starting a batch change.'
    : null

  const assertSecuredBatchReady = () => {
    const blocker = securedBatchBlockerNow()
    if (blocker) throw new Error(blocker)
  }

  const waitForEntrySync = () => {
    structureSyncPhaseRef.current = 'saving'
    setStructureSync({
      phase: 'saving',
      message: entrySyncSnapshotRef.current.pending > 0
        ? `Waiting for ${entrySyncSnapshotRef.current.pending} queued entry change${entrySyncSnapshotRef.current.pending === 1 ? '' : 's'} before saving this structural draft.`
        : 'Waiting for entry sync to become ready before saving this structural draft.',
    })
  }

  const retryStructureSync = useCallback(async () => {
    if (modeRef.current !== 'remote-owner') return
    if (structureSaveInFlightRef.current) return structureSaveInFlightRef.current
    if (structureRecoveryRef.current === 'reload') {
      structureSyncPhaseRef.current = 'error'
      setStructureSync({ phase: 'error', recovery: 'reload', message: 'Reload authoritative data before retrying this structural draft.' })
      return
    }
    if (structureRecoveryRef.current === 'reconcile' || unresolvedStructureConflictRef.current) {
      structureSyncPhaseRef.current = 'error'
      setStructureSync({ phase: 'error', recovery: 'reconcile', message: 'Review the structural conflict before saving. Your draft remains on screen.' })
      return
    }
    if (!entrySyncAllowsStructure()) {
      waitForEntrySync()
      return
    }
    const generation = structureSaveGenerationRef.current
    const pending = (async () => {
      while (generation === structureSaveGenerationRef.current) {
        const draft = documentRef.current
        const serialized = serializedStructure(draft)
        if (serialized === savedStructureRef.current) {
          structureSyncPhaseRef.current = 'synced'
          structureRecoveryRef.current = null
          setStructureSync({ phase: 'synced', message: 'Changes saved.' })
          return
        }
        if (!entrySyncAllowsStructure()) {
          waitForEntrySync()
          return
        }
        structureSyncPhaseRef.current = 'saving'
        setStructureSync({ phase: 'saving', message: 'Saving the current structural draft…' })
        try {
          const nextRevision = await saveRemoteOwnerStructure(draft, structureVersionRef.current, trustedTodayRef.current)
          if (generation !== structureSaveGenerationRef.current) return
          structureVersionRef.current = nextRevision.structureVersion
          // The current entries still belong to the existing score revision.
          // A full authoritative reload is the only operation allowed to adopt
          // a newer score revision together with its matching entries.
          setRevision((current) => ({ structureVersion: nextRevision.structureVersion, scoreRevision: current.scoreRevision }))
          savedStructureRef.current = serialized
          setAudit((current) => [`${new Date().toISOString()} · Owner structure saved at version ${nextRevision.structureVersion}`, ...current])
        } catch (error) {
          if (generation !== structureSaveGenerationRef.current) return
          const recovery = structuralSaveRecovery(error)
          structureRecoveryRef.current = recovery
          structureSyncPhaseRef.current = 'error'
          setStructureSync({ phase: 'error', recovery, message: structuralSaveFailureMessage(error) })
          setAudit((current) => [`${new Date().toISOString()} · Owner structure save failed; local draft retained`, ...current])
          throw error
        }
      }
    })()
    structureSaveInFlightRef.current = pending
    try { await pending } finally { if (structureSaveInFlightRef.current === pending) structureSaveInFlightRef.current = null }
  }, [])

  const reloadAuthoritativeStructure = useCallback(async () => {
    if (modeRef.current !== 'remote-owner') return
    if (structureSaveInFlightRef.current) throw new Error('Wait for the structural save to finish before reloading.')
    if (!entrySyncAllowsStructure()) {
      waitForEntrySync()
      throw new Error('Wait for entry sync to become ready before reloading authoritative owner data.')
    }
    const beforeMutation = documentMutationRef.current
    const draft = documentRef.current
    const draftSerialized = serializedStructure(draft)
    structureSyncPhaseRef.current = 'saving'
    setStructureSync({ phase: 'saving', message: 'Reloading authoritative owner data…' })
    try {
      const remote = await loadRemoteOwnerDocument()
      if (!remote) throw new Error('The authenticated owner document could not be reloaded.')
      if (!entrySyncAllowsStructure()) throw new Error('Entry sync changed while authoritative owner data was reloading. Retry when sync is ready.')
      if (documentMutationRef.current !== beforeMutation) throw new Error('Owner data changed while Reload was in progress. Retry Reload; your draft remains on screen.')
      structureSaveGenerationRef.current += 1
      const authoritativeSerialized = serializedStructure(remote.document)
      const preserveDraft = draftSerialized !== savedStructureRef.current
      const safeRebase = preserveDraft && authoritativeSerialized === savedStructureRef.current
      const committedLostResponse = preserveDraft && authoritativeSerialized === draftSerialized
      if (preserveDraft && !safeRebase && !committedLostResponse) {
        unresolvedStructureConflictRef.current = remote
        structureRecoveryRef.current = 'reconcile'
        structureSyncPhaseRef.current = 'error'
        setStructureSync({
          phase: 'error',
          recovery: 'reconcile',
          message: `Authoritative version ${remote.structureVersion} contains structural changes from another device. Your draft remains on screen and will not be submitted until you review the conflict.`,
        })
        setAudit((current) => [`${new Date().toISOString()} · Authoritative owner structure loaded for conflict review at version ${remote.structureVersion}; local draft retained`, ...current])
        return
      }
      const nextDocument = safeRebase
        ? { ...remote.document, target: draft.target, categories: draft.categories, operations: draft.operations }
        : remote.document
      documentRef.current = nextDocument
      documentMutationRef.current += 1
      setDocument(nextDocument)
      setAccumulationAnchor(remote.accumulationAnchor ?? firstRecordedDate(remote.document, remote.trustedToday))
      trustedTodayRef.current = remote.trustedToday
      setTrustedToday(remote.trustedToday)
      savedStructureRef.current = authoritativeSerialized
      structureVersionRef.current = remote.structureVersion
      revisionRef.current = { structureVersion: remote.structureVersion, scoreRevision: remote.scoreRevision }
      setRevision(revisionRef.current)
      unresolvedStructureConflictRef.current = null
      if (safeRebase) {
        structureRecoveryRef.current = 'retry'
        structureSyncPhaseRef.current = 'error'
        setStructureSync({ phase: 'error', recovery: 'retry', message: `Authoritative version ${remote.structureVersion} loaded without structural conflicts. Your draft remains on screen; Retry to save it.` })
      } else {
        structureRecoveryRef.current = null
        structureSyncPhaseRef.current = 'synced'
        setStructureSync({ phase: 'synced', message: 'Authoritative owner data loaded. Changes are saved.' })
      }
      setAudit((current) => [`${new Date().toISOString()} · Authoritative owner document reloaded at structure version ${remote.structureVersion}`, ...current])
    } catch (error) {
      structureRecoveryRef.current = 'reload'
      structureSyncPhaseRef.current = 'error'
      setStructureSync({ phase: 'error', recovery: 'reload', message: error instanceof Error ? error.message : 'Authoritative owner data could not be reloaded. Your draft remains on screen.' })
      throw error
    }
  }, [])

  useEffect(() => {
    let disposed = false
    const load = async () => {
      try {
        const remote = await loadRemoteOwnerDocument()
        if (disposed) return
        if (!remote) {
          if (configuredOwnerRef.current) setOwnerLoad('error')
          return
        }
        documentRef.current = remote.document
        documentMutationRef.current += 1
        setDocument(remote.document)
        setAccumulationAnchor(remote.accumulationAnchor ?? firstRecordedDate(remote.document, remote.trustedToday))
        setTrustedToday(remote.trustedToday)
        structureVersionRef.current = remote.structureVersion
        setRevision({ structureVersion: remote.structureVersion, scoreRevision: remote.scoreRevision })
        savedStructureRef.current = serializedStructure(remote.document)
        structureRecoveryRef.current = null
        unresolvedStructureConflictRef.current = null
        modeRef.current = 'remote-owner'
        setMode('remote-owner')
        structureSyncPhaseRef.current = 'synced'
        setStructureSync({ phase: 'synced', message: 'Changes saved.' })
        setAudit([`${new Date().toISOString()} · Authenticated owner document loaded`])
        setOwnerLoad('ready')
      } catch {
        if (!disposed) {
          if (configuredOwnerRef.current) setOwnerLoad('error')
          setAudit((current) => [`${new Date().toISOString()} · Owner document could not be loaded; secured changes remain unavailable`, ...current])
        }
      }
    }
    void load()
    const listener = getSupabaseClient()?.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' && modeRef.current !== 'remote-owner') void load()
      if (event === 'SIGNED_OUT' && !disposed) {
        const fixture = createFixtureDocument()
        structureSaveGenerationRef.current += 1
        structureSaveInFlightRef.current = null
        structureRecoveryRef.current = null
        unresolvedStructureConflictRef.current = null
        structureVersionRef.current = 0
        structureSyncPhaseRef.current = 'synced'
        savedStructureRef.current = serializedStructure(fixture)
        documentRef.current = fixture
        documentMutationRef.current += 1
        modeRef.current = 'synthetic-preview'
        setDocument(fixture); setAccumulationAnchor(firstRecordedDate(fixture, FIXTURE_TODAY)); setTrustedToday(FIXTURE_TODAY); setMode('synthetic-preview'); setRevision({ structureVersion: 0, scoreRevision: 0 })
        setStructureSync({ phase: 'synced', message: 'Signed out. No owner draft is being synchronized.' })
        if (configuredOwnerRef.current) setOwnerLoad('loading')
      }
    })
    return () => { disposed = true; listener?.data.subscription.unsubscribe() }
  }, [])

  useEffect(() => {
    if (mode !== 'remote-owner') return
    const serialized = serializedStructure(document)
    if (serialized === savedStructureRef.current) return
    if (structureSyncPhaseRef.current === 'error') return
    if (!entrySyncAllowsStructure()) {
      waitForEntrySync()
      return
    }
    const timer = window.setTimeout(() => {
      void retryStructureSync().catch(() => undefined)
    }, 350)
    return () => window.clearTimeout(timer)
  }, [document, entrySyncSnapshot.pending, entrySyncSnapshot.phase, mode, online, retryStructureSync, trustedToday])

  const appendAudit = (message: string) => setAudit((current) => [`${new Date().toISOString()} · ${message}`, ...current])
  const markStructureDirty = () => {
    documentMutationRef.current += 1
    if (mode === 'remote-owner' && structureSyncPhaseRef.current !== 'error') { structureSyncPhaseRef.current = 'saving'; setStructureSync({ phase: 'saving', message: 'Saving structural changes…' }) }
  }

  const value = useMemo<OwnerMatrixContextValue>(() => ({
    document,
    trustedToday,
    mode,
    revision,
    structureSync,
    securedBatchBlocker: mode !== 'remote-owner' ? null
      : !online ? 'Batch changes require an internet connection.'
      : entrySyncSnapshot.pending > 0 ? `Wait for ${entrySyncSnapshot.pending} queued entry change${entrySyncSnapshot.pending === 1 ? '' : 's'} to sync.`
      : entrySyncSnapshot.phase !== 'synced' ? 'Wait for entry sync to finish before starting a batch change.'
      : structureSync.phase !== 'synced' ? 'Wait for structural changes to finish syncing before starting a batch change.'
      : null,
    accumulationAnchor,
    audit,
    setOutcome(operationId, date, outcome) {
      if (outcome !== null && !isSelectableOutcome(outcome)) throw new Error('M is import-only and cannot be selected')
      documentMutationRef.current += 1
      setDocument((current) => {
        const entries = current.entries.filter((entry) => !(entry.operationId === operationId && entry.date === date))
        if (outcome !== null) entries.push({ operationId, date, outcome })
        return { ...current, entries }
      })
      appendAudit(`${outcome === null ? 'Cleared' : `Recorded ${outcome} for`} ${operationId} on ${date}`)
    },
    addCategory(name) {
      const clean = name.trim()
      if (!clean) return
      markStructureDirty()
      setDocument((current) => {
        const id = uniqueId('category', clean, current.categories.map((category) => category.id))
        const category: Category = { id, name: clean, activePeriods: [{ from: trustedToday, toExclusive: null }], operationOrder: [] }
        return { ...current, categories: [...current.categories, category] }
      })
      appendAudit(`Added category ${clean}`)
    },
    renameCategory(id, name) {
      const clean = name.trim()
      if (!clean) return
      markStructureDirty()
      setDocument((current) => ({ ...current, categories: current.categories.map((category) => category.id === id ? { ...category, name: clean } : category) }))
      appendAudit(`Renamed category ${id}`)
    },
    deleteCategory(id) {
      markStructureDirty()
      const categoryOperationIds = new Set(document.operations.filter((operation) => operation.categoryId === id).map((operation) => operation.id))
      const categoryHasHistory = document.entries.some((entry) => categoryOperationIds.has(entry.operationId))
      setDocument((current) => {
        const operationIds = new Set(current.operations.filter((operation) => operation.categoryId === id).map((operation) => operation.id))
        const hasHistory = current.entries.some((entry) => operationIds.has(entry.operationId))
        if (!hasHistory) return {
          ...current,
          categories: current.categories.filter((category) => category.id !== id),
          operations: current.operations.filter((operation) => !operationIds.has(operation.id)),
        }
        return {
          ...current,
          categories: current.categories.map((category) => category.id === id
            ? { ...category, activePeriods: closeActivePeriods(category.activePeriods, trustedToday) }
            : category),
          operations: current.operations.map((operation) => operationIds.has(operation.id)
            ? { ...operation, activePeriods: closeActivePeriods(operation.activePeriods, trustedToday) }
            : operation),
        }
      })
      appendAudit(categoryHasHistory ? `Archived category ${id} and preserved its history` : `Deleted history-free category ${id}`)
    },
    addOperation(draft) {
      const clean = draft.name.trim()
      if (!clean) return
      const startDate = draft.startDate ?? trustedToday
      const category = document.categories.find((candidate) => candidate.id === draft.categoryId)
      if (!category) throw new Error('Choose an existing category before creating an operation.')
      if (!isActiveOn(category.activePeriods, startDate)) {
        const solePeriod = category.activePeriods.length === 1 ? category.activePeriods[0] : undefined
        if (!solePeriod || solePeriod.toExclusive !== null || startDate >= solePeriod.from) {
          throw new Error('Category cannot be backdated across a lifecycle gap. Choose a date when it was active.')
        }
      }
      markStructureDirty()
      setDocument((current) => {
        const id = uniqueId('operation', clean, current.operations.map((operation) => operation.id))
        const operation: Operation = {
          id, categoryId: draft.categoryId, name: clean, private: draft.private,
          activePeriods: [{ from: startDate, toExclusive: null }],
          ruleVersions: [{ id: `${id}-rule-${startDate}`, effectiveFrom: startDate, rule: DEFAULT_DAILY_RULE }],
        }
        return {
          ...current,
          operations: [...current.operations, operation],
          categories: current.categories.map((category) => category.id === draft.categoryId ? {
            ...category,
            activePeriods: category.activePeriods.length === 1 && category.activePeriods[0]!.toExclusive === null && startDate < category.activePeriods[0]!.from
              ? [{ ...category.activePeriods[0]!, from: startDate }]
              : category.activePeriods,
            operationOrder: [...category.operationOrder, id],
          } : category),
        }
      })
      appendAudit(`Added ${draft.private ? 'private' : 'shareable'} daily operation ${clean} from ${startDate}`)
    },
    updateOperation(id, draft) {
      const clean = draft.name.trim()
      if (!clean) return
      const existing = document.operations.find((operation) => operation.id === id)
      const hasHistory = document.entries.some((entry) => entry.operationId === id && entry.date < trustedToday)
      if (existing && existing.categoryId !== draft.categoryId && hasHistory) {
        throw new Error('An operation with history cannot be moved to another category; archive it and create a new operation.')
      }
      markStructureDirty()
      setDocument((current) => ({
        ...current,
        operations: current.operations.map((operation) => operation.id === id ? { ...operation, name: clean, categoryId: draft.categoryId, private: draft.private } : operation),
        categories: current.categories.map((category) => ({
          ...category,
          operationOrder: category.id === draft.categoryId
            ? [...category.operationOrder.filter((operationId) => operationId !== id), id]
            : category.operationOrder.filter((operationId) => operationId !== id),
        })),
      }))
      appendAudit(`Updated operation ${id}`)
    },
    deleteOperation(id) {
      markStructureDirty()
      const operationHasHistory = document.entries.some((entry) => entry.operationId === id)
      setDocument((current) => {
        const hasHistory = current.entries.some((entry) => entry.operationId === id)
        if (!hasHistory) return {
          ...current,
          operations: current.operations.filter((operation) => operation.id !== id),
          categories: current.categories.map((category) => ({ ...category, operationOrder: category.operationOrder.filter((operationId) => operationId !== id) })),
        }
        return {
          ...current,
          operations: current.operations.map((operation) => operation.id === id
            ? { ...operation, activePeriods: closeActivePeriods(operation.activePeriods, trustedToday) }
            : operation),
        }
      })
      appendAudit(operationHasHistory ? `Archived operation ${id} and preserved its history` : `Deleted history-free operation ${id}`)
    },
    addRuleVersion(operationId, effectiveFrom, rule) {
      const operation = document.operations.find((candidate) => candidate.id === operationId)
      if (!operation) throw new Error('Choose an operation before changing its schedule.')
      markStructureDirty()
      const sameDate = operation.ruleVersions.some((version) => version.effectiveFrom === effectiveFrom)
      const updated = sameDate
        ? { ...operation, ruleVersions: operation.ruleVersions.map((version) => version.effectiveFrom === effectiveFrom ? { ...version, rule } : version) }
        : applyEffectiveRuleEdit(operation, { id: `${operationId}-rule-${effectiveFrom}`, effectiveFrom, rule }, trustedToday)
      setDocument((current) => ({ ...current, operations: current.operations.map((candidate) => candidate.id === operationId ? updated : candidate) }))
      appendAudit(`Added effective-dated rule for ${operationId} from ${effectiveFrom}`)
    },
    setTarget(target) {
      markStructureDirty()
      setDocument((current) => ({ ...current, target }))
      appendAudit(`Changed goal comparison target to ${target}`)
    },
    setAnchor(next, auditMessage) {
      markStructureDirty()
      setAccumulationAnchor(next)
      appendAudit(auditMessage)
    },
    replaceDocument(next, auditMessage) {
      markStructureDirty()
      setDocument(next)
      setAccumulationAnchor(firstRecordedDate(next, trustedToday))
      appendAudit(auditMessage)
    },
    commitEntryBatch(transform, auditMessage) {
      documentMutationRef.current += 1
      setDocument((current) => {
        const next = transform(current)
        documentRef.current = next
        return next
      })
      appendAudit(auditMessage)
    },
    acceptRevision(nextRevision) {
      structureVersionRef.current = nextRevision.structureVersion
      revisionRef.current = nextRevision
      setRevision(nextRevision)
    },
    async refreshRevision() {
      if (mode !== 'remote-owner') return revision
      assertSecuredBatchReady()
      const beforeStructure = JSON.stringify({ target: documentRef.current.target, categories: documentRef.current.categories, operations: documentRef.current.operations })
      const beforeDocument = JSON.stringify(documentRef.current)
      const beforeMutation = documentMutationRef.current
      if (beforeStructure !== savedStructureRef.current) throw new Error('Wait for structural changes to finish syncing before starting a secured workflow.')
      const remote = await loadRemoteOwnerDocument()
      if (!remote) throw new Error('The authenticated owner document could not be refreshed.')
      assertSecuredBatchReady()
      if (structureSyncPhaseRef.current !== 'synced' || documentMutationRef.current !== beforeMutation || JSON.stringify(documentRef.current) !== beforeDocument) {
        throw new Error('Owner data changed while refreshing. Wait for sync, then retry.')
      }
      const nextRevision = { structureVersion: remote.structureVersion, scoreRevision: remote.scoreRevision }
      const remoteSerialized = JSON.stringify({ target: remote.document.target, categories: remote.document.categories, operations: remote.document.operations })
      documentRef.current = remote.document
      documentMutationRef.current += 1
      setDocument(remote.document)
      setAccumulationAnchor(remote.accumulationAnchor ?? firstRecordedDate(remote.document, remote.trustedToday))
      setTrustedToday(remote.trustedToday)
      savedStructureRef.current = remoteSerialized
      structureVersionRef.current = remote.structureVersion
      revisionRef.current = nextRevision
      setRevision(nextRevision)
      setAudit((current) => [`${new Date().toISOString()} · Authoritative owner document refreshed before secured workflow`, ...current])
      return nextRevision
    },
    retryStructureSync,
    reloadAuthoritativeStructure,
    assertSecuredBatchReady,
    securedBatchSnapshot() {
      assertSecuredBatchReady()
      return { document: documentRef.current, revision: revisionRef.current }
    },
    commitTimelineOperation(input, nextRevision) {
      const currentExisting = document.operations.find((candidate) => (candidate.sourceKey ?? candidate.id) === input.sourceKey)
      const currentCategory = document.categories.find((candidate) => (candidate.sourceKey ?? candidate.id) === input.categorySourceKey)
      if (!currentCategory) throw new Error('Choose an existing category before changing an operation timeline.')
      if (!isActiveOn(currentCategory.activePeriods, input.startDate)) {
        const solePeriod = currentCategory.activePeriods.length === 1 ? currentCategory.activePeriods[0] : undefined
        if (currentExisting || !solePeriod || solePeriod.toExclusive !== null || input.startDate >= solePeriod.from) {
          throw new Error('Category cannot be backdated across a lifecycle gap. Choose a date when it was active.')
        }
      }
      documentMutationRef.current += 1
      setDocument((current) => {
        const existing = current.operations.find((candidate) => (candidate.sourceKey ?? candidate.id) === input.sourceKey)
        const category = current.categories.find((candidate) => (candidate.sourceKey ?? candidate.id) === input.categorySourceKey)!
        const operation: Operation = existing
          ? replaceScheduleTimeline({ ...existing, name: input.name, private: input.private, categoryId: category.id }, input.phases)
          : {
              id: input.sourceKey, sourceKey: input.sourceKey, categoryId: category.id,
              name: input.name, private: input.private,
              activePeriods: [{ from: input.startDate, toExclusive: null }],
              ruleVersions: canonicalSchedulePhases(input.startDate, input.phases),
            }
        const exists = existing !== undefined
        const next = {
          ...current,
          operations: exists ? current.operations.map((candidate) => candidate.id === existing.id ? operation : candidate) : [...current.operations, operation],
          categories: current.categories.map((category) => {
            const selectedCategory = category.id === operation.categoryId
            const operationOrder = selectedCategory
              ? category.operationOrder.includes(operation.id) ? category.operationOrder : [...category.operationOrder, operation.id]
              : exists ? category.operationOrder.filter((id) => id !== operation.id) : category.operationOrder
            if (!selectedCategory || exists) return { ...category, operationOrder }
            const activePeriods = category.activePeriods.length === 1 && category.activePeriods[0]!.toExclusive === null && input.startDate < category.activePeriods[0]!.from
              ? [{ ...category.activePeriods[0]!, from: input.startDate }]
              : category.activePeriods
            return { ...category, activePeriods, operationOrder }
          }),
        }
        savedStructureRef.current = JSON.stringify({ target: next.target, categories: next.categories, operations: next.operations })
        return next
      })
      structureVersionRef.current = nextRevision.structureVersion
      revisionRef.current = nextRevision
      setRevision(nextRevision)
      structureSyncPhaseRef.current = 'synced'; setStructureSync({ phase: 'synced', message: 'Changes saved.' })
      appendAudit(`Applied secured operation timeline for ${input.sourceKey} at structure version ${nextRevision.structureVersion}`)
    },
  }), [accumulationAnchor, audit, document, entrySyncSnapshot.pending, entrySyncSnapshot.phase, mode, online, reloadAuthoritativeStructure, retryStructureSync, revision, structureSync, trustedToday])

  if (ownerLoad === 'loading') return <main className="centred-page"><section className="auth-card panel" role="status"><h1>Loading owner matrix…</h1><p>Your account has been verified. Loading its secured owner document now.</p><span className="spinner" aria-hidden="true" /></section></main>
  if (ownerLoad === 'error') return <main className="centred-page"><section className="auth-card panel" role="alert"><h1>Owner data unavailable</h1><p>The authenticated owner document could not be loaded. No synthetic owner data is being shown.</p><button type="button" className="button" onClick={() => window.location.reload()}>Try again</button></section></main>
  return <OwnerMatrixContext.Provider value={value}>{children}</OwnerMatrixContext.Provider>
}

export function useOwnerMatrix(): OwnerMatrixContextValue {
  const value = useContext(OwnerMatrixContext)
  if (!value) throw new Error('useOwnerMatrix must be used inside OwnerMatrixProvider')
  return value
}

export function entryFor(document: MatrixDocument, operationId: string, date: string): Entry | undefined {
  return document.entries.find((entry) => entry.operationId === operationId && entry.date === date)
}

